import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getAxisCareClients, parseAxisCareClients, normalizeClientName as normName } from '../lib/axiscare';

const STATUS_STYLE = {
  auto:      { label: '✓ Auto-matched',        color: 'var(--success)' },
  ambiguous: { label: '⚠ Pick a match',         color: 'var(--warn)' },
  collision: { label: '⚠ ID already linked',    color: 'var(--danger)' },
  unmatched: { label: '— No AxisCare match',    color: 'var(--text3)' },
};

// Reconciles clients.axiscare_client_id against AxisCare's own client list.
// Only ever proposes changes for clients missing the field — an existing,
// manually-entered axiscare_client_id is never overwritten by this tool.
export default function AxisCareSyncModal({ onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState({});
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const [{ data: axisRaw, error: axErr }, { data: ourClients, error: dbErr }] = await Promise.all([
        getAxisCareClients(),
        supabase.from('clients').select('id, client_id, first_name, last_name, axiscare_client_id, current_status').neq('current_status', 'Discharged'),
      ]);
      if (axErr) throw new Error(axErr.message || 'AxisCare unavailable');
      if (dbErr) throw dbErr;

      const axisClients = parseAxisCareClients(axisRaw);
      if (!axisClients.length) throw new Error('AxisCare returned no clients — check the connection and try again.');

      const axisByName = {};
      axisClients.forEach(ac => {
        const key = normName(ac.firstName, ac.lastName);
        if (!key) return;
        (axisByName[key] = axisByName[key] || []).push(ac);
      });

      const takenAxisIds = {};
      (ourClients || []).forEach(c => { if (c.axiscare_client_id != null) takenAxisIds[String(c.axiscare_client_id)] = c; });

      const missing = (ourClients || []).filter(c => c.axiscare_client_id == null);
      const proposed = missing.map(c => {
        const key = normName(c.first_name, c.last_name);
        const candidates = axisByName[key] || [];
        let status, axisMatch = null, note = '';
        if (candidates.length === 1) {
          axisMatch = candidates[0];
          const collision = takenAxisIds[String(axisMatch.id)];
          if (collision) { status = 'collision'; note = `AxisCare ID ${axisMatch.id} is already linked to ${collision.client_id}`; }
          else status = 'auto';
        } else if (candidates.length > 1) {
          status = 'ambiguous'; note = `${candidates.length} AxisCare clients share this name — pick the right one`;
        } else {
          status = 'unmatched'; note = 'No AxisCare client with a matching name';
        }
        return { client: c, axisMatch, candidates, status, note };
      });

      setRows(proposed);
      setChecked(Object.fromEntries(proposed.map((r, i) => [i, r.status === 'auto'])));
    } catch (err) {
      setError(err.message || 'Failed to load AxisCare clients');
    } finally {
      setLoading(false);
    }
  };

  const pickCandidate = (i, axisId) => {
    setRows(prev => prev.map((r, idx) => {
      if (idx !== i) return r;
      const chosen = r.candidates.find(c => String(c.id) === String(axisId)) || null;
      return { ...r, axisMatch: chosen };
    }));
    setChecked(p => ({ ...p, [i]: !!axisId }));
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      let updated = 0, skipped = 0;
      for (let i = 0; i < rows.length; i++) {
        if (!checked[i] || !rows[i].axisMatch?.id) { skipped++; continue; }
        const { error } = await supabase.from('clients')
          .update({ axiscare_client_id: parseInt(rows[i].axisMatch.id, 10) })
          .eq('id', rows[i].client.id);
        if (!error) updated++; else skipped++;
      }
      setResult({ updated, skipped });
      onApplied?.();
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = Object.values(checked).filter(Boolean).length;

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }} onMouseDown={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Sync AxisCare Client IDs</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 0 }}>
            Matches our clients against AxisCare's client list by name and proposes an AxisCare ID
            for anyone missing one. Only clients with no AxisCare ID linked are touched — nothing
            already set is changed. Review each row before applying.
          </p>

          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>Loading AxisCare client list…</div>}
          {!loading && error && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 13 }}>{error}</div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>Every active client already has an AxisCare ID linked.</div>
          )}

          {!loading && !error && rows.length > 0 && !result && (
            <div style={{ maxHeight: 420, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)' }}>
                  <tr>
                    <th style={{ padding: '8px 10px', width: 28 }} />
                    {['Client', 'Proposed AxisCare match', 'Status'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const s = STATUS_STYLE[r.status];
                    const canCheck = r.status === 'auto' || (r.status === 'ambiguous' && r.axisMatch);
                    return (
                      <tr key={r.client.id} style={{ borderTop: '0.5px solid var(--border)' }}>
                        <td style={{ padding: '6px 10px' }}>
                          <input type="checkbox" checked={!!checked[i]} disabled={!canCheck}
                            onChange={e => setChecked(p => ({ ...p, [i]: e.target.checked }))} />
                        </td>
                        <td style={{ padding: '6px 10px', fontWeight: 500 }}>
                          {r.client.last_name}, {r.client.first_name}
                          <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text3)' }}>{r.client.client_id}</div>
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          {r.status === 'ambiguous' ? (
                            <select className="form-select" style={{ fontSize: 11, padding: '4px 6px' }}
                              value={r.axisMatch?.id || ''} onChange={e => pickCandidate(i, e.target.value)}>
                              <option value="">— choose —</option>
                              {r.candidates.map(c => <option key={c.id} value={c.id}>ID {c.id} — {c.firstName} {c.lastName}</option>)}
                            </select>
                          ) : r.axisMatch ? (
                            <span>AxisCare ID {r.axisMatch.id} — {r.axisMatch.firstName} {r.axisMatch.lastName}</span>
                          ) : <span style={{ color: 'var(--text3)' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 10px', fontSize: 10 }}>
                          <span style={{ color: s.color, fontWeight: 500 }} title={r.note}>{s.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {result && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--success-bg)', color: 'var(--success)', fontSize: 13 }}>
              Linked {result.updated} client{result.updated === 1 ? '' : 's'}.{result.skipped > 0 ? ` ${result.skipped} left unchanged.` : ''}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {!result ? (
            <>
              <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={loading || applying || rows.length === 0 || selectedCount === 0}
                onClick={handleApply}>
                {applying ? 'Applying…' : `Link ${selectedCount} client${selectedCount === 1 ? '' : 's'}`}
              </button>
            </>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}
